import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'ui-components/components/button';
import { FormattedMessage } from 'react-intl';

import { Modal } from 'ui-components/components/modal';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { GuidService } from 'ui-components/services/guid-service';
import { RadioCheckList } from 'ui-components/components/radio-check-list';
import { RadioCheckListItem } from 'ui-components/components/radio-check-list/radio-check-list-item';
import { FloatInput } from 'ui-components/components/float-input';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuTitleWithLabel } from 'ui-components/components/dropdown-menu-title-with-label';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { emptyArray } from 'ui-components/default-props/empty';
import { messages } from './messages';

import { ErrorMessage } from './error-message';
import style from './save-filterview-modal.module.less';

const ID_SAVE_NEW = 'ID_SAVE_NEW';
const ID_SAVE_OVERWRITE = 'ID_SAVE_OVERWRITE';

const NoViewNameErrorMessage = <FormattedMessage {...messages.noViewNameErrorMessage} />;

const ExistingViewNameErrorMessage = <FormattedMessage {...messages.existingViewNameErrorMessage} />;

export const SaveFilterViewModal = ({
  closeModal,
  savedFilterViews = emptyArray,
  saveNewFilterViewRequest,
  customFilterView,
  currentGridViewId,
  saveExistingFilterViewRequest,
}) => {
  const [selectedRadioItemId, setSelectedRadioItemId] = useState(ID_SAVE_NEW);
  const [viewToOverride, setViewToOverride] = useState();
  const [newViewName, setNewViewName] = useState('');
  const [errorMessage, setErrorMessage] = useState();

  const onNewNameValueChanged = useCallback((value) => {
    setNewViewName(value);
    setErrorMessage();
  }, []);

  const onSaveClick = useCallback(() => {
    if (selectedRadioItemId === ID_SAVE_NEW) {
      const doesExistingViewHaveName = savedFilterViews.some((view) => view.displayName === newViewName);
      if (doesExistingViewHaveName) {
        setErrorMessage('saveError');
        return;
      }

      if (newViewName === '') {
        setErrorMessage('noNameError');
        return;
      }
      const newFilterView = {
        ...customFilterView,
        displayName: newViewName,
        filterViewId: GuidService.newGuid(),
        gridViewId: currentGridViewId,
      };
      saveNewFilterViewRequest(newFilterView);
      return;
    }
    const viewWithNewFilters = {
      ...viewToOverride,
      appliedFilters: customFilterView.appliedFilters,
    };
    saveExistingFilterViewRequest(viewWithNewFilters);
  }, [
    currentGridViewId,
    customFilterView,
    newViewName,
    saveExistingFilterViewRequest,
    saveNewFilterViewRequest,
    savedFilterViews,
    selectedRadioItemId,
    viewToOverride,
  ]);

  const onOverwriteChecked = useCallback(() => {
    setSelectedRadioItemId(ID_SAVE_OVERWRITE);
    setErrorMessage();
  }, []);

  const shouldRenderSavedViews = selectedRadioItemId === ID_SAVE_OVERWRITE;
  return (
    <Modal className={style['save-filterview-modal']}>
      <ModalHeader closeModal={closeModal}>
        <FormattedMessage {...messages.saveViewModalHeader} />
      </ModalHeader>
      <ModalBody className={style['modal-body']}>
        <div className={style['modal-content']}>
          <RadioCheckList>
            <RadioCheckListItem
              id={ID_SAVE_NEW}
              isChecked={selectedRadioItemId === ID_SAVE_NEW}
              onChecked={() => setSelectedRadioItemId(ID_SAVE_NEW)}
            >
              <FormattedMessage {...messages.saveAsNewViewLabel} />
            </RadioCheckListItem>
            <RadioCheckListItem
              id={ID_SAVE_OVERWRITE}
              isChecked={selectedRadioItemId === ID_SAVE_OVERWRITE}
              isDisabled={savedFilterViews.length <= 0}
              onChecked={onOverwriteChecked}
            >
              <FormattedMessage {...messages.overwriteExistingViewLabel} />
            </RadioCheckListItem>
          </RadioCheckList>
          {shouldRenderSavedViews ? (
            <DropdownMenu className={style['dropdown-button']} dropdownMenuShouldHaveWidthOfButton>
              <DropdownMenuToggler>
                <DropdownMenuTitleWithLabel
                  label={<FormattedMessage {...messages.dropdownLabel} />}
                  title={viewToOverride ? viewToOverride.displayName : <FormattedMessage {...messages.selectFilterView} />}
                />
              </DropdownMenuToggler>
              {savedFilterViews.map((option, index) => {
                return (
                  <DropdownMenuItem key={index} onClick={() => setViewToOverride(option)}>
                    {option.displayName}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenu>
          ) : (
            <FloatInput
              label={<FormattedMessage {...messages.inputFloatLabel} />}
              maxLength={50}
              onChange={onNewNameValueChanged}
              value={newViewName}
            />
          )}
          {errorMessage && <ErrorMessage message={errorMessage === 'saveError' ? ExistingViewNameErrorMessage : NoViewNameErrorMessage} />}
        </div>
      </ModalBody>
      <ModalFooter className={style['modal-footer']}>
        <Button className={style['cancel-button']} onClick={closeModal} theme='tertiary'>
          <FormattedMessage {...messages.cancelButtonText} />
        </Button>
        <Button onClick={onSaveClick} theme='primary'>
          <FormattedMessage {...messages.saveViewButtonText} />
        </Button>
      </ModalFooter>
    </Modal>
  );
};

SaveFilterViewModal.propTypes = {
  currentGridViewId: PropTypes.string.isRequired,
  saveNewFilterViewRequest: PropTypes.func.isRequired,
  saveExistingFilterViewRequest: PropTypes.func.isRequired,
  customFilterView: PropTypes.object.isRequired,
  savedFilterViews: PropTypes.array,
  closeModal: PropTypes.func.isRequired,
};
